import React, { useCallback } from "react";
import { Box, Button, Icon, IndexTable, Text, TextField, Tooltip } from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import NiceSalesRateRecommendation from "./nice_sales_rate_recommendation";
import NiceSeasonalRecommendation from "./nice_seasonal_recommendation";
import type { EditablePurchaseOrderLineItem, IPurchaseOrder } from "../../../api_utils/types";
import { assertNumber, assertString, formatMoney, getCurrencySymbol } from "../../../helper_functions/utils";
import HorseVariant from "../../common/HorseVariant/HorseVariant";
import { humanDateFormatWithColor } from "../../../helper_functions/component_utils";
import NiceSalesRate from "../../sales_trends/support/NiceSalesRate";

import type { DraftColumn } from "./optionalColumns";
import { getCostAndMargin } from "./utils";

const oosColor = (date: Date | string): string => {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const msToDay = 1000 * 60 * 60 * 24;
  if (new Date(date).getTime() < Date.now()) {
    return "#FF0000";
  } else if (Math.ceil((new Date(date).getTime() - Date.now()) / msToDay) < 30) {
    return "#B98900";
  } else {
    return "transparent";
  }
};

export const PurchaseOrderDraftRow = React.memo(function PurchaseOrderDraftRow({
  selectedForecastingMethod,
  costExchangeRate,
  currency,
  poli,
  setPoli,
  removeVariant,
  position,
  purchaseOrder,
  readyForRecommendations,
  userCurrency,
  visibleColumns,
}: {
  readonly selectedForecastingMethod: string;
  readonly costExchangeRate?: number;
  readonly currency: string;
  readonly poli: EditablePurchaseOrderLineItem;
  readonly setPoli: (poli: EditablePurchaseOrderLineItem) => void;
  readonly removeVariant: (id: number) => void;
  readonly position: number;
  readonly purchaseOrder: IPurchaseOrder;
  readonly readyForRecommendations: boolean;
  readonly userCurrency: string;
  readonly visibleColumns: readonly DraftColumn[];
}): React.ReactElement {
  const handleCostChange = useCallback(
    (newCost: string): void => {
      const cleanCost = newCost?.replace(/[^\d.-]/g, "");
      setPoli({ ...poli, cost: cleanCost });
    },
    [poli, setPoli],
  );

  const handleQuantityChange = useCallback(
    (newQuantity: string): void => {
      const cleanQuantity = newQuantity?.replace(/[^\d.-]/g, "");
      setPoli({ ...poli, quantity: cleanQuantity });
    },
    [poli, setPoli],
  );

  const columnValues = {
    margin: getCostAndMargin(poli, costExchangeRate).margin,
    price: formatMoney(poli.price, userCurrency),
    pack_size: poli.pack_size,
    warehouse_location: poli.warehouse_location && (
      <Box maxWidth="160px">
        <Tooltip content={poli.warehouse_location}>
          <Text as="span" truncate>{poli.warehouse_location}</Text>
        </Tooltip>
      </Box>
    ),
    min: poli.min,
    supplier_sku: poli.supplier_sku && (
      <Box maxWidth="192px">
        <Tooltip content={poli.supplier_sku}>
          <Text as="span" truncate>{poli.supplier_sku}</Text>
        </Tooltip>
      </Box>
    ),
  };

  const lineTotal = assertNumber(poli.cost) * assertNumber(poli.quantity);

  const removeSelf = useCallback(() => {
    removeVariant(poli.id);
  }, [removeVariant, poli.id]);

  let orderedQuantityElement;
  let {relevant_purchase_order_quantity} = poli;
  if (relevant_purchase_order_quantity === undefined || relevant_purchase_order_quantity === null) {
    relevant_purchase_order_quantity = poli.ordered_quantity || 0;
  }
  if (relevant_purchase_order_quantity !== poli.ordered_quantity) {
    let content = "";
    if (poli.ordered_quantity && poli.ordered_quantity > 0 && relevant_purchase_order_quantity > 0) {
      content = ` ${poli.ordered_quantity} ordered but only `;
    } else if (poli.ordered_quantity && poli.ordered_quantity > 0 && relevant_purchase_order_quantity === 0) {
      content = ` ${poli.ordered_quantity} ordered but `;
    }
    content += `${relevant_purchase_order_quantity} arriving in time`;
    orderedQuantityElement = (
      <Tooltip content={content}>
        <Text as="span">
          {relevant_purchase_order_quantity}{" "}
          {poli.ordered_quantity && poli.ordered_quantity !== relevant_purchase_order_quantity
            ? `(${poli.ordered_quantity})`
            : ""}
        </Text>
      </Tooltip>
    );
  } else {
    orderedQuantityElement = poli.ordered_quantity;
  }

  return (
    <IndexTable.Row id={poli.id.toString()} key={`index-table-row:${poli.id}`} position={position}>
      <IndexTable.Cell>
        <HorseVariant key={poli.id} variant={poli} />
      </IndexTable.Cell>
      <IndexTable.Cell>{poli.tracked ? poli.currently_available || "0" : "∞"}</IndexTable.Cell>
      <IndexTable.Cell>{orderedQuantityElement}</IndexTable.Cell>
      {selectedForecastingMethod === "sales_rate" && (
        <IndexTable.Cell>
          <NiceSalesRate
            available_days={poli.available_days}
            sales_quantity={poli.sales_quantity}
            sales_rate={poli.sales_rate}
          />
        </IndexTable.Cell>
      )}
      <IndexTable.Cell>
        {selectedForecastingMethod === "sales_rate" ? (
          <NiceSalesRateRecommendation
            purchaseOrder={purchaseOrder}
            purchaseOrderLineItem={poli}
            readyForRecommendations={readyForRecommendations}
          />
        ) : (
          <NiceSeasonalRecommendation
            purchaseOrderLineItem={poli}
            readyForRecommendations={readyForRecommendations}
          />
        )}
      </IndexTable.Cell>

      <IndexTable.Cell>
        {readyForRecommendations ? (
          poli.oos_date ? (
            humanDateFormatWithColor(new Date(poli.oos_date), oosColor(poli.oos_date))
          ) : (
            ""
          )
        ) : (
          <Tooltip content="Please wait while we finish importing your store">
            <span>-</span>
          </Tooltip>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div className="w120">
          <TextField
            align="right"
            autoComplete="off"
            id={poli.id.toString()}
            key={poli.id}
            label="Cost"
            labelHidden
            name="cost"
            onChange={handleCostChange}
            placeholder="0.00"
            prefix={getCurrencySymbol(currency)}
            type="currency"
            value={assertString(poli.cost)}
          />
        </div>
      </IndexTable.Cell>
      {visibleColumns.map((column) => <IndexTable.Cell key={column.id}>{columnValues[column.id]}</IndexTable.Cell>)}
      <IndexTable.Cell>
        <div className="w120">
          <TextField
            autoComplete="off"
            id={poli.id.toString()}
            key={poli.id}
            label="Quantity"
            labelHidden
            name="quantity"
            onChange={handleQuantityChange}
            placeholder="0"
            type="integer"
            value={assertString(poli.quantity)}
          />
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div className="w120">{formatMoney(lineTotal, currency)}</div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Button icon={<Icon source={XIcon} />} onClick={removeSelf} variant="plain" />
      </IndexTable.Cell>
    </IndexTable.Row>
  );
});

export default PurchaseOrderDraftRow;
